import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAdminArmario } from '../../../hooks/useAdminArmario';
import { useClienteCalendario } from '../../../hooks/useClienteCalendario';
import { useClienteRecomendaciones } from '../../../hooks/useClienteRecomendaciones';
import { useClientePerfil } from '../../../hooks/useClientePerfil';
import { useAdminClientePerfil, type ClientePerfil, type MedidasCorporales, type CombinacionPersonalizada } from '../../../hooks/useAdminClientePerfil';
import { useAdminPerfilHistory } from '../../../hooks/useAdminPerfilHistory';
import { useAdminClientesAsesoria } from '../../../hooks/useAdminClientesAsesoria';
import { mockPrendas, mockLooksCalendario, mockRecomendaciones } from '../../../mocks/clienteArmario';
import ClienteSelector from './perfil/ClienteSelector';
import ClienteInfoCard from './perfil/ClienteInfoCard';
import PerfilEditor from './perfil/PerfilEditor';
import PerfilPreviewPanel from './perfil/PerfilPreviewPanel';
import { emptyMedidas, buildPerfilDraft } from './perfil/perfilUtils';
import { useAdminAsesoriaContext } from '../../../context/AdminAsesoriaContext';

interface Props {
  selectedClienteId?: number;
}

export default function AdminPerfilCliente({ selectedClienteId }: Props) {
  const { setSelectedClienteId } = useAdminAsesoriaContext();
  const { data: clientesData, refresh: refreshClientes } = useAdminClientesAsesoria();
  const [selectedCliente, setSelectedCliente] = useState<number>(selectedClienteId || 0);
  const [clientes, setClientes] = useState(clientesData);

  useEffect(() => {
    setClientes(clientesData);
    if (selectedClienteId && selectedClienteId > 0) {
      setSelectedCliente(selectedClienteId);
    } else if (clientesData.length > 0 && selectedCliente === 0) {
      setSelectedCliente(clientesData[0].id);
    }
  }, [clientesData, selectedClienteId, selectedCliente]);

  // Sync selected client back to shared context
  useEffect(() => {
    if (selectedCliente > 0) {
      setSelectedClienteId(selectedCliente);
    }
  }, [selectedCliente, setSelectedClienteId]);

  const prendasCliente = mockPrendas.filter((p) => p.cliente_id === selectedCliente);
  const looksCliente = mockLooksCalendario.filter((l) => l.cliente_id === selectedCliente);
  const recCliente = mockRecomendaciones.filter((r) => r.cliente_id === selectedCliente);

  const { data: prendasDb } = useAdminArmario(prendasCliente, selectedCliente || undefined);
  const { data: looksDb } = useClienteCalendario(looksCliente, selectedCliente || undefined);
  const { data: recDb } = useClienteRecomendaciones(recCliente, selectedCliente || undefined);

  useClientePerfil(
    selectedCliente,
    prendasDb.length > 0 ? prendasDb : prendasCliente,
    looksDb.length > 0 ? looksDb : looksCliente,
    recDb.length > 0 ? recDb : recCliente
  );

  const {
    perfil: adminPerfil,
    upsert: upsertPerfil,
    refresh: refreshPerfil,
    loading: loadingPerfil,
    error: hookError,
  } = useAdminClientePerfil(selectedCliente || undefined);

  const {
    changes: perfilChanges,
    recordChange,
    refresh: refreshHistory,
  } = useAdminPerfilHistory(selectedCliente || undefined);

  // --- Local state ---
  const [medidas, setMedidas] = useState<MedidasCorporales>(emptyMedidas);
  const [paletteColors, setPaletteColors] = useState<string[]>([]);
  const [tonoPiel, setTonoPiel] = useState('');
  const [estiloPersonal, setEstiloPersonal] = useState('');
  const [notasEstilista, setNotasEstilista] = useState('');
  const [ocasionesPreferidas, setOcasionesPreferidas] = useState<string[]>([]);
  const [prendasEvitar, setPrendasEvitar] = useState<string[]>([]);
  const [combinacionesPersonalizadas, setCombinacionesPersonalizadas] = useState<CombinacionPersonalizada[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const syncingRef = useRef(false);

  // Sync from DB
  useEffect(() => {
    if (syncingRef.current) return;
    if (adminPerfil) {
      setPaletteColors(adminPerfil.paleta_colores ?? []);
      setTonoPiel(adminPerfil.tono_piel ?? '');
      setEstiloPersonal(adminPerfil.estilo_personal ?? '');
      setNotasEstilista(adminPerfil.notas_estilista ?? '');
      setOcasionesPreferidas(adminPerfil.ocasiones_preferidas ?? []);
      setPrendasEvitar(adminPerfil.prendas_a_evitar ?? []);
      setMedidas(adminPerfil.medidas_corporales ?? emptyMedidas);
      setCombinacionesPersonalizadas(adminPerfil.combinaciones_personalizadas ?? []);
    } else {
      setPaletteColors([]);
      setTonoPiel('');
      setEstiloPersonal('');
      setNotasEstilista('');
      setOcasionesPreferidas([]);
      setPrendasEvitar([]);
      setMedidas(emptyMedidas);
      setCombinacionesPersonalizadas([]);
    }
  }, [adminPerfil]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSavedOk(false);
    syncingRef.current = true;

    try {
      if (adminPerfil) {
        const campos = [
          { key: 'paleta_colores', anterior: adminPerfil.paleta_colores, nuevo: paletteColors },
          { key: 'tono_piel', anterior: adminPerfil.tono_piel, nuevo: tonoPiel || null },
          { key: 'estilo_personal', anterior: adminPerfil.estilo_personal, nuevo: estiloPersonal || null },
          { key: 'notas_estilista', anterior: adminPerfil.notas_estilista, nuevo: notasEstilista || null },
          { key: 'ocasiones_preferidas', anterior: adminPerfil.ocasiones_preferidas, nuevo: ocasionesPreferidas.length > 0 ? ocasionesPreferidas : null },
          { key: 'prendas_a_evitar', anterior: adminPerfil.prendas_a_evitar, nuevo: prendasEvitar.length > 0 ? prendasEvitar : null },
          { key: 'medidas_corporales', anterior: adminPerfil.medidas_corporales, nuevo: medidas },
          { key: 'combinaciones_personalizadas', anterior: adminPerfil.combinaciones_personalizadas, nuevo: combinacionesPersonalizadas.length > 0 ? combinacionesPersonalizadas : null },
        ];
        for (const campo of campos) {
          await recordChange(campo.key, campo.anterior, campo.nuevo);
        }
      }

      const result = await upsertPerfil({
        paleta_colores: paletteColors,
        tono_piel: tonoPiel || null,
        estilo_personal: estiloPersonal || null,
        notas_estilista: notasEstilista || null,
        ocasiones_preferidas: ocasionesPreferidas.length > 0 ? ocasionesPreferidas : null,
        prendas_a_evitar: prendasEvitar.length > 0 ? prendasEvitar : null,
        medidas_corporales: medidas,
        combinaciones_personalizadas: combinacionesPersonalizadas.length > 0 ? combinacionesPersonalizadas : null,
      });

      if (!result.success) {
        setSaveError(result.error || 'Error al guardar el perfil');
        return;
      }

      await refreshHistory();
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2000);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Error al guardar el perfil');
    } finally {
      syncingRef.current = false;
    }
  }, [upsertPerfil, paletteColors, tonoPiel, estiloPersonal, notasEstilista, ocasionesPreferidas, prendasEvitar, medidas, combinacionesPersonalizadas, adminPerfil, recordChange, refreshHistory]);

  useEffect(() => {
    if (hookError) setSaveError(hookError);
  }, [hookError]);

  const previewPerfil = buildPerfilDraft(
    adminPerfil,
    paletteColors,
    tonoPiel,
    estiloPersonal,
    notasEstilista,
    ocasionesPreferidas,
    prendasEvitar,
    medidas,
    combinacionesPersonalizadas
  );

  const prendasParaCombinar = prendasDb.length > 0 ? prendasDb : prendasCliente;
  const prendasQueCoincidenPaleta = useMemo(() => {
    const paleta = (previewPerfil.paleta_colores ?? []).map((c) => c.toLowerCase());
    return prendasParaCombinar.filter((p) => paleta.some((col) => p.color.toLowerCase().includes(col)));
  }, [prendasParaCombinar, previewPerfil.paleta_colores]);

  const currentCliente = clientes.find((c) => c.id === selectedCliente);

  return (
    <div className="space-y-6">
      <ClienteSelector
        selectedCliente={selectedCliente}
        onChange={(id) => setSelectedCliente(id)}
        loadingPerfil={loadingPerfil}
        savedOk={savedOk}
        onSave={handleSave}
        onRefresh={() => { refreshClientes(); refreshPerfil(); }}
      />

      {(saveError || hookError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="font-sans text-red-600 text-sm">{saveError || hookError}</p>
        </div>
      )}

      <ClienteInfoCard
        cliente={currentCliente ? {
          id: currentCliente.id,
          nombre: currentCliente.nombre,
          email: currentCliente.email,
          telefono: currentCliente.telefono,
          activo: currentCliente.activo,
        } : undefined}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <PerfilEditor
          adminPerfil={adminPerfil}
          paletteColors={paletteColors}
          setPaletteColors={setPaletteColors}
          tonoPiel={tonoPiel}
          setTonoPiel={setTonoPiel}
          estiloPersonal={estiloPersonal}
          setEstiloPersonal={setEstiloPersonal}
          notasEstilista={notasEstilista}
          setNotasEstilista={setNotasEstilista}
          ocasionesPreferidas={ocasionesPreferidas}
          setOcasionesPreferidas={setOcasionesPreferidas}
          prendasEvitar={prendasEvitar}
          setPrendasEvitar={setPrendasEvitar}
          medidas={medidas}
          setMedidas={setMedidas}
          combinacionesPersonalizadas={combinacionesPersonalizadas}
          setCombinacionesPersonalizadas={setCombinacionesPersonalizadas}
          prendasQueCoincidenPaleta={prendasQueCoincidenPaleta}
        />

        <PerfilPreviewPanel
          clienteId={selectedCliente}
          previewPerfil={previewPerfil}
          perfilChanges={perfilChanges}
          prendasDb={prendasDb}
          prendasCliente={prendasCliente}
          recDb={recDb}
          recCliente={recCliente}
          looksDb={looksDb}
          looksCliente={looksCliente}
        />
      </div>
    </div>
  );
}
